import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../axiosConfig';
import TrainerLayout from './TrainerLayout';
import '../../styles/CreateSurvey.css';

const CreateSurvey = () => {
    const { schoolId } = useParams();
    const navigate = useNavigate();

    const [title, setTitle] = useState('');
    const [questions, setQuestions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const addQuestion = () => {
        setQuestions([
            ...questions,
            {
                questionText: '',
                questionType: 'TEXT',
                answerOptions: [],
                questionIndex: questions.length,
                allowTextInput: false
            }
        ]);
    };

    const removeQuestion = (index) => {
        const updatedQuestions = [...questions];
        updatedQuestions.splice(index, 1);

        // Update question indices after removal
        updatedQuestions.forEach((q, idx) => {
            q.questionIndex = idx;
        });

        setQuestions(updatedQuestions);
    };

    const handleQuestionChange = (index, field, value) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index][field] = value;

        // Reset answerOptions if switching to TEXT or DATE
        if (field === 'questionType') {
            if (value === 'TEXT' || value === 'DATE') {
                updatedQuestions[index].answerOptions = [];
                updatedQuestions[index].allowTextInput = false;
            } else if (value === 'SINGLE_CHOICE' || value === 'MULTIPLE_CHOICE' || value === 'CHOICE_WITH_TEXT') {
                // Ensure answerOptions is initialized as an array if switching to choice types
                if (!Array.isArray(updatedQuestions[index].answerOptions)) {
                    updatedQuestions[index].answerOptions = [];
                }
                // Add an empty option if there are none
                if (updatedQuestions[index].answerOptions.length === 0) {
                    updatedQuestions[index].answerOptions.push('');
                }

                // Enable text input for CHOICE_WITH_TEXT
                if (value === 'CHOICE_WITH_TEXT') {
                    updatedQuestions[index].allowTextInput = true;
                } else {
                    updatedQuestions[index].allowTextInput = false;
                }
            }
        }

        setQuestions(updatedQuestions);
    };

    const handleOptionChange = (qIndex, oIndex, value) => {
        const updatedQuestions = [...questions];
        updatedQuestions[qIndex].answerOptions[oIndex] = value;
        setQuestions(updatedQuestions);
    };

    const addOption = (qIndex) => {
        const updatedQuestions = [...questions];
        updatedQuestions[qIndex].answerOptions.push('');
        setQuestions(updatedQuestions);
    };

    const removeOption = (qIndex, oIndex) => {
        const updatedQuestions = [...questions];
        updatedQuestions[qIndex].answerOptions.splice(oIndex, 1);
        setQuestions(updatedQuestions);
    };

    const handleSubmit = async () => {
        if (!title.trim()) {
            alert('Proszę podać tytuł ankiety');
            return;
        }

        if (questions.length === 0) {
            alert('Proszę dodać przynajmniej jedno pytanie');
            return;
        }

        // Validate questions
        for (const question of questions) {
            if (!question.questionText.trim()) {
                alert('Wszystkie pytania muszą mieć treść');
                return;
            }

            if ((question.questionType === 'SINGLE_CHOICE' ||
                    question.questionType === 'MULTIPLE_CHOICE' ||
                    question.questionType === 'CHOICE_WITH_TEXT') &&
                question.answerOptions.length === 0) {
                alert('Pytania wyboru muszą mieć przynajmniej jedną opcję odpowiedzi');
                return;
            }
        }

        const surveyData = {
            title,
            schoolId,
            questions: questions.map((q, index) => ({
                questionText: q.questionText,
                questionType: q.questionType,
                questionIndex: q.questionIndex,
                answerOptions: q.answerOptions
            })),
            isActive: true
        };

        setIsSubmitting(true);
        try {
            await axios.post(`/api/v1/survey/create/${schoolId}`, surveyData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });
            alert('Ankieta została dodana!');
            navigate(`/survey/school/${schoolId}/all`);
        } catch (err) {
            console.error('Error creating survey:', err);
            alert('Błąd podczas tworzenia ankiety.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <TrainerLayout>
            <div className="cs-container">
                <div className="cs-header">
                    <h1 className="cs-title">Tworzenie ankiety</h1>
                </div>

                <div className="cs-section">
                    <label className="cs-label">Tytuł ankiety</label>
                    <input
                        type="text"
                        className="cs-input-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Wpisz tytuł ankiety"
                    />
                </div>

                <div className="cs-section">
                    <h2 className="cs-section-title">Pytania</h2>

                    {questions.map((question, qIndex) => (
                        <div key={qIndex} className="cs-question-card">
                            <div className="cs-question-header">
                                <span className="cs-question-number">Pytanie {qIndex + 1}</span>
                                <button
                                    onClick={() => removeQuestion(qIndex)}
                                    className="cs-btn-remove-question"
                                    title="Usuń pytanie"
                                >
                                    ✕
                                </button>
                            </div>

                            <input
                                type="text"
                                className="cs-input-question"
                                value={question.questionText}
                                onChange={(e) => handleQuestionChange(qIndex, 'questionText', e.target.value)}
                                placeholder="Wpisz treść pytania"
                            />

                            <div className="cs-question-type">
                                <label className="cs-type-label">Typ pytania:</label>
                                <select
                                    value={question.questionType}
                                    onChange={(e) => handleQuestionChange(qIndex, 'questionType', e.target.value)}
                                    className="cs-select-type"
                                >
                                    <option value="TEXT">Tekst</option>
                                    <option value="DATE">Data</option>
                                    <option value="SINGLE_CHOICE">Jednokrotny wybór</option>
                                    <option value="MULTIPLE_CHOICE">Wielokrotny wybór</option>
                                    <option value="CHOICE_WITH_TEXT">Wybór + tekst</option>
                                </select>
                            </div>

                            {(question.questionType === 'SINGLE_CHOICE' ||
                                question.questionType === 'MULTIPLE_CHOICE' ||
                                question.questionType === 'CHOICE_WITH_TEXT') && (
                                <div className="cs-options-container">
                                    {question.answerOptions.length === 0 && (
                                        <p className="cs-no-options">Dodaj opcje odpowiedzi</p>
                                    )}

                                    {question.answerOptions.map((option, oIndex) => (
                                        <div key={oIndex} className="cs-option-row">
                                            <span className="cs-option-number">{oIndex + 1}.</span>
                                            <input
                                                type="text"
                                                className="cs-input-option"
                                                value={option}
                                                onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                                                placeholder="Wpisz opcję odpowiedzi"
                                            />
                                            <button
                                                onClick={() => removeOption(qIndex, oIndex)}
                                                className="cs-btn-remove-option"
                                                title="Usuń opcję"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                    <button onClick={() => addOption(qIndex)} className="cs-btn-add-option">
                                        + Dodaj opcję
                                    </button>
                                </div>
                            )}

                            {question.questionType === 'CHOICE_WITH_TEXT' && (
                                <div className="cs-text-input-option">
                                    <label className="cs-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={question.allowTextInput}
                                            onChange={(e) => handleQuestionChange(qIndex, 'allowTextInput', e.target.checked)}
                                        />
                                        <span>Pozwól na wpisanie własnej odpowiedzi</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Add Question Button - Dotted Box */}
                    <div className="cs-add-question-box" onClick={addQuestion}>
                        <div className="cs-add-icon">+</div>
                        <p className="cs-add-text">
                            {questions.length === 0
                                ? 'Brak pytań. Kliknij tutaj, aby rozpocząć'
                                : 'Dodaj kolejne pytanie'}
                        </p>
                    </div>
                </div>

                <div className="cs-footer-actions">
                    <button
                        onClick={() => navigate(`/survey/school/${schoolId}/all`)}
                        className="cs-btn-cancel-large"
                    >
                        Anuluj
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="cs-btn-save-large"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Zapisywanie...' : 'Zapisz ankietę'}
                    </button>
                </div>
            </div>
        </TrainerLayout>
    );
};

export default CreateSurvey;